import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { container, dataGridStyles, iconButtonStyle } from 'pages/DashboardHelpers';
import { useDispatch, useSelector } from 'react-redux';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import { DEFAULT_PAGE_SIZE } from 'constants/configConstants';
import { getRowClassName, pageSizeOptions } from 'pages/ocplcDashboard/ocplcHelpers';
import CustomPagination from 'pages/ocplcDashboard/CustomPagination';
import AddChoiceOrSubSectionDialog from './addChoiceOrSubSection';
import { clearOcUiSpecFilters, filterOcUiSpec, getOcUiSpec } from 'actions/userActions';
import OCUISpecColumns from './ocUiSpecsColumns';
import { EditDialogForUiSpec } from './editDialogForUiSpec';


function OCUISpecs() {
    const dispatch = useDispatch();

    const [dialogType, setDialogType] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);


    const loading = useSelector(state => state.user?.loading);
    const filters = useSelector(state => state.user?.ocUiSpecFilters || {});
    const filteredOcUiSpec = useSelector(state => state.user?.filteredOcUiSpec) || [];
    const ocUiSpecNested = useSelector(state => state.user?.ocUiSpec?.ocUiSpecNested) || [];
    const email = useSelector(state => state.user?.kbUser?.email);

    const handleCloseDialog = useCallback(() => {
        setDialogType(null);
        setSelectedRow(null);
    }, []);

    const handleChange = useCallback((col, value) => {
        dispatch(filterOcUiSpec({ ...col, searchText: value }));
    }, []);

    const handleClearFilters = () => {
        dispatch(clearOcUiSpecFilters());
    };

    const handleEdit = useCallback((row) => {
        setSelectedRow(row);
        setDialogType('edit');
    }, []);


    useEffect(() => {
        dispatch(getOcUiSpec());
    }, [dispatch]);

    const columns = useMemo(
        () =>
            OCUISpecColumns({
                filters,
                handleChange,
                handleEdit
            }),
        [filters, handleChange, handleEdit]
    );

    const visibleRows = useMemo(
        () => filteredOcUiSpec.filter((row) => row?.sequence !== 0 && row?.sequence !== '0'),
        [filteredOcUiSpec]
    );

    const createButtonConfig = [
        {
            type: 'choice',
            title: 'Create new Choice',
            description: 'Add new Choice',
            label: 'Add Choice'
        },
        {
            type: 'subSection',
            title: 'Create new Sub Section',
            description: 'Add new Sub Section',
            label: 'Add Sub Section'
        }
    ];

    return (
        <Box sx={{ ...container, minWidth: '100%', marginTop: 2 }} className='oc-ui-specs-container'>
            <Typography variant='h5'>OC UI Specs</Typography>
            <Box
                className='oc-ui-specs-header'
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    mb: 2,
                    mt: 2
                }}
            >
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {createButtonConfig.map((item) => (
                        <Box key={item.type} sx={{ display: 'flex', alignItems: 'center', padding: '8px 16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                            <Box sx={{ mr: 2 }}>
                                <Typography sx={{ fontSize: "19px" }}>{item.title}</Typography>
                                <Typography sx={{ color: '#b8b8b8', fontSize: "13px" }}>{item.description}</Typography>
                            </Box>
                            <Button variant="text" onClick={() => setDialogType(item.type)}><AddIcon fontSize='small' /> {item.label}</Button>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        color="primary"
                        sx={iconButtonStyle}
                        disableRipple
                        onClick={handleClearFilters}
                        endIcon={<FilterAltOffIcon />}
                    >
                        Clear Filters
                    </Button>
                </Box>
            </Box>
            <DataGrid
                columns={columns}
                rows={visibleRows}
                loading={loading}
                getRowId={(row) => [row.productLine, row.tabCategory, row.subSectionId, row.choiceId || 'NO_CHOICE', row.sequence || '0'].join('|')}
                columnHeaderHeight={90}
                rowHeight={48}
                className='dashboard-data-grid'
                getRowClassName={getRowClassName}
                headerClassName='dashboard-grid-headers'
                slots={{ pagination: props => <CustomPagination {...props} pageSizeOptions={pageSizeOptions} /> }}
                disableRowSelectionOnClick
                initialState={{ pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE } } }}
                sx={dataGridStyles}
                disableColumnFilter
                disableColumnMenu
                hideFooterSelectedRowCount
            />
            {dialogType && (
                <AddChoiceOrSubSectionDialog
                    open={true}
                    onClose={handleCloseDialog}
                    ocUiSpecNested={ocUiSpecNested}
                    email={email}
                    dialogType={dialogType}
                />
            )}
            {dialogType === 'edit' && selectedRow && (
                <EditDialogForUiSpec
                    open={true}
                    handleClose={handleCloseDialog}
                    ocUiSpecNested={ocUiSpecNested}
                    email={email}
                    selectedRow={selectedRow}
                />
            )

            }

        </Box>
    );
}

export default OCUISpecs;
